import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/cms/invoice-scanning/scans';
export const getInvoiceScansEndpointIdentifier = endpoint;

export interface IInvoiceScanData {
  id: number;
  orderId: number;
  uploadedBy: number;
  originalFileName: string;
  s3Url: string;
  mimeType: string;
  fileSize: number;
  status: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  vendorName: string | null;
  subtotal: number | null;
  taxAmount: number | null;
  totalAmount: number | null;
  currency: string | null;
  validationStatus: string;
  processedAt: string | null;
  createdAt: string;
  order: { id: number; restaurantId: number };
  uploadedByUser: { id: number; name: string };
  itemCount: string;
}

export interface IInvoiceScansResponse {
  data: IInvoiceScanData[];
  count: number;
  total: number;
  page: number;
  pageCount: number;
  hasNext: boolean;
  hasPrev: boolean;
}

type TData = IInvoiceScansResponse;
type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  { page: TParam; limit: TParam; status: TParam; restaurantId: TParam }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetInvoiceScansApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { status, restaurantId } = getPageParams();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, {
        params: { page, limit, status, restaurantId },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper([endpoint, { page, limit, status, restaurantId }], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
